import { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  ChevronRight,
  Clock3,
  Loader2,
  ReceiptText,
  Search,
  ShoppingBag,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { publicMenuApi } from "../../api";
import { useAlerts } from "../../components/feedback/AlertProvider";
import { useLanguage } from "../../contexts/LanguageContext";
import "../../styles/PublicMenu/public-customer-orders.css";
const PUBLIC_MENU_ORDERS_PREFIX = "public_menu_orders_";
const MAX_VISIBLE_ORDER_PRODUCTS = 4;
const DEFAULT_PUBLIC_MENU_THEME = {
  theme: "light",
  primary_color: "#51766E",
  background_color: "#F8FAF9",
  text_color: "#333333",
  surface_color: "#FFFFFF",
  accent_color: "#D7E3E0",
  border_radius: "large",
  card_style: "elevated",
  card_shadow: "soft",
  font_family: "system",
};
const hexToRgb = (color, fallback) => {
  if (!color) return fallback;
  const trimmed = String(color).trim();
  if (trimmed.startsWith("rgb")) {
    const matches = trimmed.match(/\d+(?:\\.\d+)?/g);
    if (matches?.length >= 3) return matches.slice(0, 3).join(", ");
  }
  const normalized = trimmed.replace("#", "");
  const fullHex =
    normalized.length === 3
      ? normalized.split("").map((char) => `${char}${char}`).join("")
      : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) return fallback;
  return `${parseInt(fullHex.slice(0, 2), 16)}, ${parseInt(fullHex.slice(2, 4), 16)}, ${parseInt(fullHex.slice(4, 6), 16)}`;
};
const buildCustomerOrdersTheme = (menuData) => {
  const menu = menuData?.menu || {};
  const establishment = menuData?.establishment || {};
  const template =
    menuData?.template ||
    menu?.template ||
    menu?.menu_template ||
    {};
  return {
    ...DEFAULT_PUBLIC_MENU_THEME,
    ...(template?.config || {}),
    ...(establishment?.menu_design_settings || {}),
    ...(menuData?.design_settings || {}),
    ...(menu?.design_settings || {}),
    ...(menu?.menu_design_settings || {}),
  };
};
const ORDER_STATUS_OPTIONS = [
  "all",
  "pending",
  "accepted",
  "preparing",
  "ready",
  "delivered",
  "cancelled",
];
const ORDER_STATUS_KEYS = [
  "pending",
  "accepted",
  "preparing",
  "ready",
  "delivered",
  "cancelled",
];
const ORDER_TYPE_KEYS = ["dine_in", "takeaway", "delivery"];
const normalizeOrderStatus = (status) => {
  const value = String(status || "").toLowerCase();
  return ORDER_STATUS_KEYS.includes(value) ? value : "pending";
};
const normalizeOrderType = (type) => {
  const value = String(type || "").toLowerCase();
  return ORDER_TYPE_KEYS.includes(value) ? value : "takeaway";
};
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};
const normalizeTranslations = (translations) => {
  if (Array.isArray(translations)) return translations.filter(Boolean);
  if (translations && typeof translations === "object") {
    return Object.entries(translations).map(([locale, translation]) => {
      if (!translation || typeof translation !== "object") {
        return { locale, name: translation ? String(translation) : "" };
      }
      return {
        locale: translation.locale || locale,
        ...translation,
      };
    });
  }
  return [];
};
const getTranslationValue = (item, field, language, fallback = "fr") => {
  if (!item) return "";
  if (item?.translations?.[language]?.[field]) {
    return item.translations[language][field];
  }
  if (item?.translations?.[fallback]?.[field]) {
    return item.translations[fallback][field];
  }
  const translations = normalizeTranslations(item?.translations);
  const selectedTranslation =
    translations.find((translation) => translation.locale === language) ||
    translations.find((translation) => translation.locale === fallback) ||
    translations.find((translation) => translation.locale === "en") ||
    translations.find((translation) => translation.locale === "ar") ||
    translations.find((translation) => translation.locale === "es") ||
    translations[0];
  return selectedTranslation?.[field] || item?.[field] || "";
};
const formatPrice = (value, currency = "MAD") => {
  const amount = Number(value || 0);
  return `${amount.toFixed(2)} ${currency}`;
};
const formatOrderDate = (dateValue, language) => {
  if (!dateValue) return "";
  try {
    return new Intl.DateTimeFormat(language || undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateValue));
  } catch {
    return "";
  }
};
const getOrderItems = (order) => {
  return toArray(
    order?.items ||
      order?.order_items ||
      order?.products ||
      order?.cart_items ||
      order?.lines,
  );
};
const getOrderItemProductId = (item) => {
  return (
    item?.product_id ||
    item?.product?.id ||
    item?.product?.slug ||
    item?.product_slug ||
    item?.product_snapshot?.product_id ||
    item?.product_snapshot?.id ||
    ""
  );
};
const getOrderItemName = (item, language, t) => {
  return (
    item?.product_snapshot?.name ||
    getTranslationValue(item?.product, "name", language) ||
    item?.product_name ||
    item?.name ||
    t("publicMenu.customerOrders.product")
  );
};
const getOrderItemQuantity = (item) => {
  const quantity = Number(item?.quantity || item?.qty || 1);
  return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
};
function PublicCustomerOrders() {
  const { establishmentSlug, menuSlug } = useParams();
  const menuStorageId = `${establishmentSlug || ""}__${menuSlug || ""}`;
  const publicMenuPath = `/${encodeURIComponent(establishmentSlug || "")}/${encodeURIComponent(menuSlug || "")}`;
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [customerOrdersTheme, setCustomerOrdersTheme] = useState(DEFAULT_PUBLIC_MENU_THEME);
  const ordersStorageKey = `${PUBLIC_MENU_ORDERS_PREFIX}${menuStorageId}`;
  const notify = (type, message) => {
    showAlert({
      type,
      title: type === "success" ? t("alerts.success") : t("alerts.error"),
      message,
    });
  };
  const customerOrdersThemeStyle = useMemo(
    () => ({
      "--public-menu-primary": customerOrdersTheme.primary_color || "#51766E",
      "--public-menu-bg": customerOrdersTheme.background_color || "#F8FAF9",
      "--public-menu-text": customerOrdersTheme.text_color || "#333333",
      "--public-menu-surface": customerOrdersTheme.surface_color || "#FFFFFF",
      "--public-menu-accent": customerOrdersTheme.accent_color || "#D7E3E0",
      "--public-menu-primary-rgb": hexToRgb(
        customerOrdersTheme.primary_color,
        "81, 118, 110",
      ),
      "--public-menu-bg-rgb": hexToRgb(
        customerOrdersTheme.background_color,
        "248, 250, 249",
      ),
      "--public-menu-text-rgb": hexToRgb(
        customerOrdersTheme.text_color,
        "51, 51, 51",
      ),
      "--public-menu-surface-rgb": hexToRgb(
        customerOrdersTheme.surface_color,
        "255, 255, 255",
      ),
      "--public-menu-accent-rgb": hexToRgb(
        customerOrdersTheme.accent_color,
        "215, 227, 224",
      ),
    }),
    [customerOrdersTheme],
  );
  const customerOrdersThemeClassName = [
    "customer-orders-page",
    `customer-orders-page--theme-${customerOrdersTheme.theme || "light"}`,
    `customer-orders-page--radius-${customerOrdersTheme.border_radius || "large"}`,
    `customer-orders-page--card-${customerOrdersTheme.card_style || "elevated"}`,
    `customer-orders-page--shadow-${customerOrdersTheme.card_shadow || "soft"}`,
    `customer-orders-page--font-${customerOrdersTheme.font_family || "system"}`,
  ].join(" ");
  const getLocalOrders = () => {
    try {
      return JSON.parse(localStorage.getItem(ordersStorageKey) || "[]");
    } catch {
      return [];
    }
  };
  const saveLocalOrders = (nextOrders) => {
    try {
      localStorage.setItem(ordersStorageKey, JSON.stringify(nextOrders));
    } catch {
      // Local storage can fail in private browsing. The UI still works in memory.
    }
  };
  const fetchOrders = async ({ showResult = false, silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await publicMenuApi.getCustomerOrders(establishmentSlug, menuSlug);
      const remoteOrders = toArray(response.data?.data || response.data || []);
      const nextOrders = remoteOrders;
      setOrders(nextOrders);
      saveLocalOrders(nextOrders);
      if (showResult) {
        notify("success", t("publicMenu.customerOrders.refreshSuccess"));
      }
    } catch {
      const localOrders = getLocalOrders();
      setOrders(localOrders);
      if (showResult) {
        notify("error", t("publicMenu.customerOrders.loadError"));
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };
  useEffect(() => {
    fetchOrders();
  }, [establishmentSlug, menuSlug, ordersStorageKey]);
  useEffect(() => {
    let cancelled = false;
    const fetchMenuTheme = async () => {
      try {
        const response = await publicMenuApi.getMenu(establishmentSlug, menuSlug);
        const menuData = response.data?.data || null;
        if (!cancelled && menuData) {
          setCustomerOrdersTheme(buildCustomerOrdersTheme(menuData));
        }
      } catch {
        if (!cancelled) {
          setCustomerOrdersTheme(DEFAULT_PUBLIC_MENU_THEME);
        }
      }
    };
    fetchMenuTheme();
    return () => {
      cancelled = true;
    };
  }, [establishmentSlug, menuSlug]);
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      fetchOrders({ silent: true });
    }, 6000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [establishmentSlug, menuSlug, ordersStorageKey]);
  const filteredOrders = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return orders.filter((order) => {
      const orderNumber = String(order.order_number || "").toLowerCase();
      const customerName = String(order.customer_name || "").toLowerCase();
      const orderType = normalizeOrderType(order.order_type);
      const phone =
        orderType === "dine_in"
          ? ""
          : String(order.customer_phone || "").toLowerCase();
      const status = normalizeOrderStatus(order.status);
      const itemNames = getOrderItems(order)
        .map((item) => getOrderItemName(item, language, t))
        .join(" ")
        .toLowerCase();
      const matchesStatus =
        selectedStatus === "all" || status === selectedStatus;
      const matchesSearch =
        !normalizedSearch ||
        orderNumber.includes(normalizedSearch) ||
        customerName.includes(normalizedSearch) ||
        phone.includes(normalizedSearch) ||
        itemNames.includes(normalizedSearch);
      return matchesStatus && matchesSearch;
    });
  }, [orders, searchTerm, selectedStatus, language, t]);
  const totalOrders = orders.length;
  const activeOrders = orders.filter((order) =>
    ["pending", "accepted", "preparing", "ready"].includes(
      normalizeOrderStatus(order.status),
    ),
  ).length;
  const deliveredOrders = orders.filter(
    (order) => normalizeOrderStatus(order.status) === "delivered",
  ).length;
  if (loading) {
    return (
      <main className={`${customerOrdersThemeClassName} customer-orders-page--loading`} style={customerOrdersThemeStyle}>
        <div className="customer-orders-loading-card">
          <Loader2 size={34} />
          <h1>{t("publicMenu.customerOrders.loading")}</h1>
          <p>{t("publicMenu.customerOrders.loadingHint")}</p>
        </div>
      </main>
    );
  }
  return (
    <main className={customerOrdersThemeClassName} style={customerOrdersThemeStyle}>
      <section className="customer-orders-shell">
        <header className="customer-orders-hero">
          <div className="customer-orders-hero__content">
            <span className="customer-orders-hero__icon">
              <ShoppingBag size={28} />
            </span>
            <div>
              <p>{t("publicMenu.customerOrders.eyebrow")}</p>
              <h1>{t("publicMenu.customerOrders.title")}</h1>
              <span>{t("publicMenu.customerOrders.description")}</span>
            </div>
          </div>
          <div className="customer-orders-hero__stats">
            <article>
              <strong>{totalOrders}</strong>
              <span>{t("publicMenu.customerOrders.total")}</span>
            </article>
            <article>
              <strong>{activeOrders}</strong>
              <span>{t("publicMenu.customerOrders.active")}</span>
            </article>
            <article>
              <strong>{deliveredOrders}</strong>
              <span>{t("publicMenu.customerOrders.delivered")}</span>
            </article>
          </div>
        </header>
        <section className="customer-orders-controls">
          <label>
            <Search size={18} />
            <input
              type="search"
              value={searchTerm}
              placeholder={t("publicMenu.customerOrders.searchPlaceholder")}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </label>
          <div className="customer-orders-status-tabs">
            {ORDER_STATUS_OPTIONS.map((status) => (
              <button
                key={status}
                type="button"
                className={selectedStatus === status ? "active" : ""}
                onClick={() => setSelectedStatus(status)}
              >
                {status === "all"
                  ? t("common.all")
                  : t(`orders.status.${status}`)}
              </button>
            ))}
          </div>
        </section>
        <section className="customer-orders-list">
          {filteredOrders.map((order) => {
            const orderId = order.id || order.order_number;
            const orderStatus = normalizeOrderStatus(order.status);
            const orderType = normalizeOrderType(order.order_type);
            const orderItems = getOrderItems(order);
            const visibleItems = orderItems.slice(0, MAX_VISIBLE_ORDER_PRODUCTS);
            const hiddenItemsCount = Math.max(
              0,
              orderItems.length - MAX_VISIBLE_ORDER_PRODUCTS,
            );
            return (
              <article
                key={orderId}
                className={`customer-order-card customer-order-card--${orderStatus} customer-order-card--${orderType}`}
              >
                <div className="customer-order-card__content">
                  <div className="customer-order-card__left">
                    <span className="customer-order-card__badge">
                      <ReceiptText size={19} />
                    </span>
                    <div>
                      <Link
                        to={`${publicMenuPath}/orders/${orderId}`}
                        className="customer-order-card__main-link"
                      >
                        {order.order_number || `#${orderId}`}
                      </Link>
                      <p>{order.customer_name || t("publicMenu.anonymous")}</p>
                      <div className="customer-order-card__meta">
                        {order.created_at && (
                          <small>
                            <CalendarDays size={14} />
                            {formatOrderDate(order.created_at, language)}
                          </small>
                        )}
                        <small>
                          <Clock3 size={14} />
                          {t(`orders.types.${orderType}`)}
                        </small>
                      </div>
                    </div>
                  </div>
                  {visibleItems.length > 0 && (
                    <div className="customer-order-products">
                      <span className="customer-order-products__title">
                        {t("publicMenu.customerOrders.products")}
                      </span>
                      <div className="customer-order-products__list">
                        {visibleItems.map((item, itemIndex) => {
                          const productLinkId = getOrderItemProductId(item);
                          const itemName = getOrderItemName(item, language, t);
                          const quantity = getOrderItemQuantity(item);
                          const productLabel = `${
                            quantity > 1 ? `${quantity} × ` : ""
                          }${itemName}`;
                          if (!productLinkId) {
                            return (
                              <span
                                key={`${itemName}-${itemIndex}`}
                                className="customer-order-product-chip customer-order-product-chip--disabled"
                              >
                                {productLabel}
                              </span>
                            );
                          }
                          return (
                            <Link
                              key={`${productLinkId}-${itemIndex}`}
                              to={`${publicMenuPath}/products/${encodeURIComponent(productLinkId)}`}
                              className="customer-order-product-chip"
                              aria-label={`${t(
                                "publicMenu.customerOrders.openProductDetails",
                              )}: ${itemName}`}
                            >
                              {productLabel}
                            </Link>
                          );
                        })}
                        {hiddenItemsCount > 0 && (
                          <span className="customer-order-product-chip customer-order-product-chip--more">
                            +{hiddenItemsCount} {t("publicMenu.customerOrders.moreProducts")}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="customer-order-card__right">
                  <small>{t(`orders.status.${orderStatus}`)}</small>
                  <strong>
                    {formatPrice(order.total_amount, order.currency || "MAD")}
                  </strong>
                  <Link
                    to={`${publicMenuPath}/orders/${orderId}`}
                    className="customer-order-card__details-link"
                    aria-label={t("publicMenu.customerOrders.viewOrder")}
                  >
                    <span>{t("publicMenu.customerOrders.viewOrder")}</span>
                    <ChevronRight size={18} />
                  </Link>
                </div>
              </article>
            );
          })}
          {!filteredOrders.length && (
            <div className="customer-orders-empty">
              <span>
                <ShoppingBag size={34} />
              </span>
              <h2>{t("publicMenu.customerOrders.empty")}</h2>
              <p>{t("publicMenu.customerOrders.emptyHint")}</p>
            </div>
          )}
        </section>
      </section>
    </main>
  );
}
export default PublicCustomerOrders;
